const GLYPHS = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];
const TEXT_VS = '︎'; // force text (not emoji) presentation

/** 4-point sparkle path centred on (x, y). */
const sparkle = (x: number, y: number, r: number, w = r * 0.18) =>
  `M${x} ${y - r}L${x + w} ${y - w}L${x + r} ${y}L${x + w} ${y + w}L${x} ${y + r}L${x - w} ${y + w}L${x - r} ${y}L${x - w} ${y - w}Z`;

/**
 * Brand mark (owner's logo, 5 Nov 2026), redrawn as vector so it stays sharp and transparent at every size:
 * crescent moon, double zodiac ring, faceted 4-point compass star, rising candlesticks and an orbit arrow.
 * `detailed` adds the zodiac glyphs and orbit dots (use for sizes ≥ 96px).
 */
export function BrandMark({ size = 48, detailed = size >= 96 }: { size?: number; detailed?: boolean }) {
  const c = 60;
  const candles = Array.from({ length: 7 }, (_, i) => ({ x: 41 + i * 6.2, y: 75 - i * 4.9, h: 6 + (i % 3) * 1.4 }));
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" aria-hidden="true" className="brand-mark">
      <defs>
        <linearGradient id="bm-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFF1C6" />
          <stop offset=".35" stopColor="#F2D27E" />
          <stop offset=".7" stopColor="#D7AC58" />
          <stop offset="1" stopColor="#9C6F24" />
        </linearGradient>
        <radialGradient id="bm-sky" cx=".5" cy=".5" r=".5">
          <stop offset="0" stopColor="#2A1D62" />
          <stop offset="1" stopColor="#0B1230" />
        </radialGradient>
      </defs>
      {/* sky disc + rings */}
      <circle cx={c} cy={c} r="33" fill="url(#bm-sky)" />
      <circle cx={c} cy={c} r="44" fill="none" stroke="url(#bm-gold)" strokeWidth="2.4" />
      <circle cx={c} cy={c} r="33" fill="none" stroke="url(#bm-gold)" strokeWidth="1.2" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = ((i * 30 + 15) * Math.PI) / 180;
        return <line key={i} x1={c + 33 * Math.cos(a)} y1={c + 33 * Math.sin(a)} x2={c + 44 * Math.cos(a)} y2={c + 44 * Math.sin(a)} stroke="#D7AC58" strokeWidth=".8" strokeOpacity=".8" />;
      })}
      {detailed && GLYPHS.map((g, i) => {
        const a = ((i * 30 - 90) * Math.PI) / 180;
        return (
          <text key={g} x={c + 38.6 * Math.cos(a)} y={c + 38.6 * Math.sin(a)} fill="#F0D79A" fontSize="7" textAnchor="middle" dominantBaseline="central" fontFamily="'Segoe UI Symbol','Noto Sans Symbols 2','DejaVu Sans',serif">
            {g + TEXT_VS}
          </text>
        );
      })}
      {detailed && (
        <g fill="none" stroke="#D7AC58" strokeWidth=".7" strokeOpacity=".8">
          <path d="M28.5 20.5A50 50 0 0 1 47 11.7" />
          <path d="M73 11.7A50 50 0 0 1 91.5 20.5" />
          <circle cx="47" cy="11.7" r="1" fill="#F0D79A" />
          <circle cx="73" cy="11.7" r="1" fill="#F0D79A" />
        </g>
      )}
      {/* diagonal minor points */}
      <path d="M60 60L80 40L63.5 56.5ZM60 60L80 80L63.5 63.5ZM60 60L40 80L56.5 63.5ZM60 60L40 40L56.5 56.5Z" fill="url(#bm-gold)" opacity=".9" />
      <path d="M60 60L80 40L56.5 56.5ZM60 60L80 80L63.5 56.5ZM60 60L40 80L63.5 63.5ZM60 60L40 40L56.5 63.5Z" fill="url(#bm-gold)" opacity=".55" />
      {/* main compass star with bevel facets */}
      <path d="M60 12L66 54L109 60L66 66L60 109L54 66L11 60L54 54Z" fill="url(#bm-gold)" />
      <path d="M60 60L60 12L66 54ZM60 60L109 60L66 66ZM60 60L60 109L54 66ZM60 60L11 60L54 54Z" fill="#6B4710" opacity=".35" />
      {/* crescent */}
      <path d="M56.5 2.5A7.5 7.5 0 1 0 67.5 12A6 6 0 1 1 56.5 2.5Z" fill="url(#bm-gold)" />
      <path d={sparkle(60, 15.5, 4)} fill="#FFF1C6" />
      {/* side + bottom sparkles */}
      <path d={sparkle(6, 60, 6)} fill="url(#bm-gold)" />
      <path d={sparkle(114, 60, 6)} fill="url(#bm-gold)" />
      <path d={sparkle(60, 112, 5)} fill="url(#bm-gold)" />
      {/* rising candlesticks */}
      <g stroke="#0B1230" strokeWidth=".7">
        {candles.map((k, i) => (
          <g key={i}>
            <line x1={k.x} y1={k.y - k.h / 2 - 2.6} x2={k.x} y2={k.y + k.h / 2 + 2.4} stroke="#F2D27E" strokeWidth="1" />
            <rect x={k.x - 2} y={k.y - k.h / 2} width="4" height={k.h} rx=".6" fill="url(#bm-gold)" />
          </g>
        ))}
      </g>
      {/* orbit swoosh with arrow */}
      <path d="M95 44C72 56 40 70 24 78" fill="none" stroke="url(#bm-gold)" strokeWidth="1.1" opacity=".75" />
      <path d="M24 78C10 86 22 92 44 84C64 77 84 62 96 46" fill="none" stroke="url(#bm-gold)" strokeWidth="3.4" strokeLinecap="round" />
      <path d="M104 34L91 39.5L99.5 48Z" fill="url(#bm-gold)" />
    </svg>
  );
}

/** Logo lockup: mark + Thai wordmark + ASTRO TRADER, following the owner's horizontal logo. */
export function BrandLockup({ size = 44, className = '' }: { size?: number; className?: string }) {
  return (
    <span className={`brand-lockup ${className}`}>
      <BrandMark size={size} />
      <span className="brand-text">
        <span className="brand-th">โหราเทรดเดอร์</span>
        <span className="brand-en"><span className="brand-rule" aria-hidden="true" />ASTRO TRADER<span className="brand-rule" aria-hidden="true" /></span>
      </span>
    </span>
  );
}

export function ZodiacWheel({ className = '' }: { className?: string }) {
  const c = 200;
  const ticks = Array.from({ length: 72 }, (_, i) => i * 5);
  return (
    <svg viewBox="0 0 400 400" className={`zodiac ${className}`} aria-hidden="true">
      <defs>
        <radialGradient id="zw-glow" cx=".5" cy=".5" r=".5">
          <stop offset="0" stopColor="#FFE9B0" stopOpacity=".95" />
          <stop offset=".18" stopColor="#F0D79A" stopOpacity=".45" />
          <stop offset=".6" stopColor="#D7AC58" stopOpacity=".08" />
          <stop offset="1" stopColor="#D7AC58" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx={c} cy={c} r="190" fill="url(#zw-glow)" opacity=".55" />
      <g className="zodiac-spin" fill="none" stroke="#D7AC58" strokeWidth="1.4">
        <circle cx={c} cy={c} r="190" />
        <circle cx={c} cy={c} r="182" strokeOpacity=".6" />
        <circle cx={c} cy={c} r="142" />
        <circle cx={c} cy={c} r="134" strokeOpacity=".5" />
        <circle cx={c} cy={c} r="78" strokeOpacity=".7" />
        {ticks.map((d) => (
          <line key={d} x1={c} y1={c - 190} x2={c} y2={c - (d % 30 === 0 ? 170 : 184)} transform={`rotate(${d} ${c} ${c})`} strokeOpacity=".7" />
        ))}
        {GLYPHS.map((_, i) => (
          <line key={`s${i}`} x1={c} y1={c - 142} x2={c} y2={c - 182} transform={`rotate(${i * 30 + 15} ${c} ${c})`} />
        ))}
        {GLYPHS.map((g, i) => {
          const a = ((i * 30) * Math.PI) / 180;
          const x = c + Math.sin(a) * 162;
          const y = c - Math.cos(a) * 162;
          return (
            <text key={g} x={x} y={y} fill="#F0D79A" stroke="none" fontSize="24" textAnchor="middle" dominantBaseline="central" fontFamily="'Noto Sans Symbols','Segoe UI Symbol','DejaVu Sans',FreeSerif,serif">
              {g + TEXT_VS}
            </text>
          );
        })}
      </g>
      <g stroke="#F0D79A" strokeWidth="1" className="zodiac-rays">
        {Array.from({ length: 24 }, (_, i) => (
          <line key={i} x1={c} y1={c} x2={c} y2={c - (i % 2 ? 70 : 128)} transform={`rotate(${i * 15} ${c} ${c})`} strokeOpacity={i % 2 ? 0.35 : 0.7} />
        ))}
      </g>
      <path d={`M${c} ${c - 40} L${c + 9} ${c - 9} L${c + 40} ${c} L${c + 9} ${c + 9} L${c} ${c + 40} L${c - 9} ${c + 9} L${c - 40} ${c} L${c - 9} ${c - 9}Z`} fill="#F7E3AE" />
      <circle cx={c} cy={c} r="7" fill="#fff" />
    </svg>
  );
}

/** Deterministic decorative candlestick series (not market data). */
export function Candles({ count = 22, className = '', trend = 1.6 }: { count?: number; className?: string; trend?: number }) {
  let seed = 7;
  const rnd = () => ((seed = (seed * 9301 + 49297) % 233280) / 233280);
  const w = 14;
  const H = 200;
  let price = 150;
  const bars = Array.from({ length: count }, (_, i) => {
    const open = price;
    const close = open - trend * 2 - (rnd() - 0.42) * 26;
    const hi = Math.min(open, close) - rnd() * 14;
    const lo = Math.max(open, close) + rnd() * 14;
    price = Math.max(20, Math.min(185, close));
    return { x: i * (w + 8) + 4, open, close, hi, lo };
  });
  return (
    <svg viewBox={`0 0 ${count * (w + 8)} ${H}`} className={`candles ${className}`} aria-hidden="true" preserveAspectRatio="xMidYMax meet">
      {bars.map((b, i) => {
        const up = b.close < b.open;
        const col = up ? '#2FD3A4' : '#F0506E';
        return (
          <g key={i} opacity={0.35 + (i / count) * 0.65}>
            <line x1={b.x + w / 2} x2={b.x + w / 2} y1={b.hi} y2={b.lo} stroke={col} strokeWidth="2" />
            <rect x={b.x} y={Math.min(b.open, b.close)} width={w} height={Math.max(3, Math.abs(b.close - b.open))} fill={col} rx="2" />
          </g>
        );
      })}
    </svg>
  );
}

export function Crown({ size = 44 }: { size?: number }) {
  return (
    <svg width={size} height={size * 0.75} viewBox="0 0 64 48" aria-hidden="true">
      <defs>
        <linearGradient id="cr-g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FCE7AF" />
          <stop offset="1" stopColor="#C8963F" />
        </linearGradient>
      </defs>
      <path d="M6 14 20 26 32 6 44 26 58 14 52 40H12Z" fill="url(#cr-g)" stroke="#FFF1C9" strokeWidth="1.2" strokeLinejoin="round" />
      <rect x="11" y="40" width="42" height="5" rx="2" fill="#C8963F" />
      <circle cx="32" cy="6" r="3.2" fill="#FFF1C9" />
      <circle cx="6" cy="14" r="2.8" fill="#FFF1C9" />
      <circle cx="58" cy="14" r="2.8" fill="#FFF1C9" />
    </svg>
  );
}

export type IconName = 'calendar' | 'live' | 'play' | 'arrow' | 'lock' | 'menu' | 'close' | 'user' | 'info' | 'moon' | 'book' | 'chart' | 'shield' | 'check' | 'users' | 'bell';

export function Icon({ name }: { name: IconName }) {
  const p = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true' } as const;
  switch (name) {
    case 'moon':
      return <svg {...p}><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" /><path d="M17 3v3M15.5 4.5h3" /></svg>;
    case 'book':
      return <svg {...p}><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5Z" /><path d="M4 20.5A2.5 2.5 0 0 0 6.5 23H20v-5M9 7h7M9 10.5h5" /></svg>;
    case 'chart':
      return <svg {...p}><path d="M3 3v18h18" /><path d="m7 15 4-4 3 3 5-6" /><path d="M15 8h4v4" /></svg>;
    case 'shield':
      return <svg {...p}><path d="M12 3 4.5 6v5.5c0 4.6 3.2 8.4 7.5 9.5 4.3-1.1 7.5-4.9 7.5-9.5V6Z" /><path d="m8.8 12 2.2 2.2 4.4-4.4" /></svg>;
    case 'check':
      return <svg {...p}><path d="m5 12.5 4.2 4.2L19 7" /></svg>;
    case 'users':
      return <svg {...p}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14a6.5 6.5 0 0 1 3.5 6" /></svg>;
    case 'bell':
      return <svg {...p}><path d="M6 9a6 6 0 0 1 12 0c0 6 2.5 7.5 2.5 7.5h-17S6 15 6 9Z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>;
    case 'calendar':
      return <svg {...p}><rect x="3" y="5" width="18" height="16" rx="3" /><path d="M8 3v4M16 3v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" /></svg>;
    case 'live':
      return <svg {...p}><circle cx="12" cy="12" r="2" fill="currentColor" /><path d="M7.8 7.8a6 6 0 0 0 0 8.4M16.2 7.8a6 6 0 0 1 0 8.4M5 5a10 10 0 0 0 0 14M19 5a10 10 0 0 1 0 14" /></svg>;
    case 'play':
      return <svg {...p}><path d="M8 5.5v13l10.5-6.5Z" fill="currentColor" /></svg>;
    case 'arrow':
      return <svg {...p}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
    case 'lock':
      return <svg {...p}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>;
    case 'menu':
      return <svg {...p}><path d="M4 7h16M4 12h16M4 17h16" /></svg>;
    case 'close':
      return <svg {...p}><path d="M6 6l12 12M18 6 6 18" /></svg>;
    case 'user':
      return <svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>;
    case 'info':
      return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11v6M12 7.5h.01" /></svg>;
  }
}
